import React from 'react';

/**
 * BodyBaseItem component that handles different sizes with responsive width behavior
 * and supports swappable components
 * 
 * @param {Object} props - Component props
 * @param {('XL'|'L'|'M'|'S')} [props.size='M'] - Size variant
 * @param {string} [props.type='Standard'] - Type of the item
 * @param {React.ReactNode} [props.children] - Content to display
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {number} [props.minWidth=120] - Minimum width in pixels before truncation
 * @param {React.ReactNode} [props.swappableComponent=null] - Optional component to swap in place of text content
 * @param {boolean} [props.isSwapped=false] - Whether to show the swappable component instead of text
 * @returns {React.ReactElement} The BodyBaseItem component
 */
const BodyBaseItem = ({ 
  size = 'M', 
  type = 'Standard', 
  children, 
  className = "",
  minWidth = 120,
  swappableComponent = null,
  isSwapped = false
}) => {
  // Set appropriate height class based on size
  const heightClass = 
    size === 'XL' ? 'h-16' :  // 64px (h-16)
    size === 'L' ? 'h-12' :   // 48px (h-12)
    size === 'M' ? 'h-10' :   // 40px (h-10)
    size === 'S' ? 'h-8' : 'h-10'; // 32px (h-8), default to M
  
  // Adjust padding based on size
  const paddingClass = 
    size === 'XL' ? 'p-4' :
    size === 'L' ? 'p-3' :
    size === 'M' ? 'px-4 py-2' :
    size === 'S' ? 'px-3 py-2' : 'px-4 py-2'; // default to M
  
  // Adjust text styling based on size
  const textClass = 
    size === 'XL' ? 'text-sm' :
    size === 'L' ? 'text-sm' :
    size === 'M' ? 'text-xs' :
    size === 'S' ? 'text-xs' : 'text-sm'; // default to M
  
  // Normal font weight for content items (vs bold for headers)
  const fontWeightClass = 'font-normal';

  return (
    <div 
      className={`max-w-full ${heightClass} Type-${type} bg-white flex items-center ${className}`}
      style={{
        minWidth: `${minWidth}px`,  // Set minimum width before content truncation
        overflow: 'hidden',         // Hide content that exceeds the width
        flexShrink: 1,              // Allow shrinking
        flexGrow: 1,                // Allow growing
        flexBasis: 0,               // Equal width distribution
      }}
    >
      <div className={`w-full ${paddingClass} flex items-center`}>
        {isSwapped && swappableComponent ? (
          // Render the swappable component if isSwapped is true and a component is provided
          <div className="flex items-center justify-center">
            {swappableComponent}
          </div>
        ) : (
          // Otherwise render the regular text content
          <div className={`text-neutral-900 ${textClass} ${fontWeightClass} font-['Arial'] leading-normal truncate`}>
            {children || "Content"}
          </div>
        )}
      </div>
    </div>
  );
};

export default BodyBaseItem;